import './CoverageDetails.css';
import type { ScoredRoute } from './RouteResults';

interface CoverageDetailsProps {
  route: ScoredRoute;
  dataAge?: number; // in seconds
  controllers?: Controller[];
}

interface Controller {
  callsign: string;
  type: string;
  facility: string;
}

function formatDataAge(seconds: number): string {
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
}

export default function CoverageDetails({ route, dataAge = 0, controllers = [] }: CoverageDetailsProps) {
  const isStale = dataAge > 300; // 5 minutes

  return (
    <div className="coverage-details">
      <div className="coverage-header">
        <h3>Coverage Details</h3>
        <div className={`data-age ${isStale ? 'stale' : ''}`}>
          {isStale && <span className="warning-icon">⚠</span>}
          <span>Data: {formatDataAge(dataAge)}</span>
        </div>
      </div>

      {isStale && (
        <div className="stale-warning">
          <strong>Warning:</strong> Coverage data is stale. Information may not reflect current ATC staffing.
        </div>
      )}

      <div className="score-breakdown">
        <h4>Score Breakdown</h4>
        <table className="breakdown-table">
          <tbody>
            <tr>
              <td className="breakdown-label">Departure Staffed</td>
              <td className="breakdown-value">
                <span className={`status-badge ${route.scoreBreakdown.departureStaffed ? 'yes' : 'no'}`}>
                  {route.scoreBreakdown.departureStaffed ? 'Yes' : 'No'}
                </span>
              </td>
            </tr>
            <tr>
              <td className="breakdown-label">Arrival Staffed</td>
              <td className="breakdown-value">
                <span className={`status-badge ${route.scoreBreakdown.arrivalStaffed ? 'yes' : 'no'}`}>
                  {route.scoreBreakdown.arrivalStaffed ? 'Yes' : 'No'}
                </span>
              </td>
            </tr>
            <tr>
              <td className="breakdown-label">Enroute Coverage</td>
              <td className="breakdown-value">
                {route.scoreBreakdown.enrouteCoveragePercent.toFixed(1)}%
              </td>
            </tr>
            <tr>
              <td className="breakdown-label">CTR Coverage</td>
              <td className="breakdown-value">
                {route.scoreBreakdown.ctrCoveragePercent.toFixed(1)}%
              </td>
            </tr>
            <tr>
              <td className="breakdown-label">Longest Gap</td>
              <td className="breakdown-value">
                {route.scoreBreakdown.longestGapNm.toFixed(0)} nm
              </td>
            </tr>
            <tr className="total-row">
              <td className="breakdown-label">Total Score</td>
              <td className="breakdown-value">
                <strong>{route.coverageScore}</strong> / 100
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {controllers.length > 0 && (
        <div className="active-controllers">
          <h4>Active Controllers Along Route</h4>
          <div className="controllers-list">
            {controllers.map((controller, index) => (
              <div key={`${controller.callsign}-${index}`} className="controller-item">
                <span className="controller-callsign">{controller.callsign}</span>
                <span className="controller-type">{controller.type}</span>
                <span className="controller-facility">{controller.facility}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {controllers.length === 0 && (
        <div className="no-controllers">
          <p>No active controllers found along this route.</p>
        </div>
      )}
    </div>
  );
}
